import { assertMover, isPassable } from './clearance.js';
import { cellAt } from './grid.js';
import { createFlowField } from './pathfinding.js';
/**
 * "Walk as close as you can" — the nearest cell to a point that an entity can
 * both stand on and actually get to.
 *
 * `findPath` answers `null` for a click on rock, on a walled-off cavern, or on a
 * gap too narrow for this body. That is the correct *simulation* answer and a
 * poor game: the player cannot tell a refused click from a dropped one, and D2
 * itself walks as far towards the point as it can and stops there (`TODO.md`
 * #92). This module is the missing half — it turns any point at all into a cell
 * a route can be found to, so the caller asks one question and always has
 * somewhere to send the entity.
 *
 * It is deliberately *not* a repair of a bad route. Nothing here pushes an
 * entity anywhere or edits a path; it answers a question about the map, and the
 * caller then paths to the answer through the ordinary pathfinder. An entity
 * that cannot be routed still simply does not move.
 *
 * ## Nearest, defined
 *
 * "Nearest" is ambiguous the moment two cells tie, and a tie broken differently
 * on the client than on the server is a desync that only shows up on the maps
 * where it matters. So the order is total, and stated:
 *
 *   1. **The smallest squared distance from the candidate cell to the target's
 *      cell.** Cells are whole numbers, so this comparison is a sum of two
 *      integer squares — exact in floating point, and therefore identical
 *      everywhere it is computed. Squared, because only the comparison matters.
 *   2. **Then the smallest travel cost from the entity.** Of two spots equally
 *      close to the click, the entity walks to the one it reaches soonest;
 *      taking the long way round to an equally good cell is the kind of thing a
 *      player reports as a bug.
 *   3. **Then the lowest row-major index.** Reading order — the same last-resort
 *      tie-break `firstFreePosition` and the dungeon's region labelling already
 *      use. Nothing about the geometry can choose between mirror images, so the
 *      scan order does, in one fixed direction.
 *
 * Distance is measured to the *cell* the target falls in, like every other
 * position query in core, so a click a hair either side of a cell seam cannot
 * change the answer. A target off the map is measured to just the same — the
 * cell it would be, which sends the entity towards the edge of the world rather
 * than refusing an off-map click.
 *
 * ## Cost
 *
 * One Dijkstra sweep of the map, borrowed from `createFlowField`: it already
 * solves "how far is every cell from here" under the mover's own erosion, and
 * an unreachable cell keeps the `Infinity` that says so. That is a click-rate
 * answer, not a per-tick one — a caller that wants this every tick for a whole
 * pack should keep one field and sample it, which is what flow fields are for.
 */
/**
 * The nearest cell to `target` that a mover standing at `from` can occupy and
 * reach, or `null` when the entity is not standing anywhere it could stand.
 *
 * `null` is the one refusal, and it is not about the target: any point at all
 * has an answer as long as the entity itself is somewhere legal, because the
 * entity's own cell is always a candidate. The world's guarded placement makes
 * that failure impossible by construction (`TODO.md` #73); it is returned rather
 * than thrown because "nowhere to walk" is an outcome, not a broken program.
 *
 * A mover built for a different map, or a nonsense radius, *is* a broken
 * program and throws — the same line `findPath` draws.
 */
export const nearestReachable = (grid, from, target, mover) => {
    assertMover(grid, mover);
    if (!isPassable(grid, from, mover))
        return null;
    // Rooted at the entity: `distances` is then the cost of walking *to* each
    // cell, and `Infinity` marks every cell this body cannot reach or cannot fit
    // in — rock included, since a cell that is never expanded is never relaxed.
    const field = createFlowField(grid, from, mover);
    const targetX = Math.floor(target.x);
    const targetY = Math.floor(target.y);
    let best = null;
    let bestGap = Number.POSITIVE_INFINITY;
    let bestTravel = Number.POSITIVE_INFINITY;
    // Row-major, so the scan itself supplies the last tie-break: a candidate only
    // ever displaces one that is strictly better ranked, never one that ties.
    for (let index = 0; index < field.distances.length; index += 1) {
        const travel = field.distances[index] ?? Number.POSITIVE_INFINITY;
        if (travel === Number.POSITIVE_INFINITY)
            continue;
        const cell = cellAt(grid, index);
        const dx = cell.x - targetX;
        const dy = cell.y - targetY;
        const gap = dx * dx + dy * dy;
        if (gap > bestGap)
            continue;
        if (gap === bestGap && travel >= bestTravel)
            continue;
        best = cell;
        bestGap = gap;
        bestTravel = travel;
    }
    return best;
};
//# sourceMappingURL=reachable.js.map